/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Toast } from 'sharedUi/Toast';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { TabView, TabPanel } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { formatDate } from 'sharedUi/dateUtils';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import FormSelect from '../../components/forms/form-select';
import { ragsService, type RagVersionCreateRequest } from '../../services/rags.service';
import { pipelinesService } from '../../services/pipelines.service';
import { projectsService } from '../../services/projects.service';
import { promptsService } from '../../services/prompts.service';
import { aiModelsService } from '../../services/ai-models.service';
import { evalSuitesService } from '../../services/eval-suites.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { versionTone } from './rag-status';
import { EmptyState, PageSkeleton, Rise, StatusChip } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { PANEL, PRETTY } from '../../components/common/ui-kit';

const fth = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted';
const ftd = 'px-3 py-2.5 align-middle';

const masterSchema = z.object({
  ragCode: z.string().min(1, 'RAG code is required').max(150).trim(),
  name: z.string().min(1, 'Name is required').max(200).trim(),
  description: z.string().optional().or(z.literal('')),
  status: z.string().optional(),
  appId: z.string().optional().or(z.literal('')),
  defaultEvalSuiteRef: z.string().optional().or(z.literal('')),
  requireEvalPass: z.boolean(),
});
type MasterForm = z.infer<typeof masterSchema>;

/** A labelled switch. The whole row is the label, so the hit area is the row,
 *  not the 44px control at its end. */
const Switch = ({ label, hint, value, onChange, disabled = false }: {
  label: string; hint?: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) => (
  <label className={`flex items-start justify-between gap-3 py-2.5 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
    <span className="min-w-0">
      <span className="block text-sm text-ink">{label}</span>
      {hint && <span className="mt-0.5 block text-xs leading-relaxed text-ink-muted" style={PRETTY}>{hint}</span>}
    </span>
    <InputSwitch checked={value} onChange={(e) => onChange(!!e.value)} disabled={disabled} className="mt-0.5 flex-none" />
  </label>
);
const Num = ({ label, value, onChange, min, max, hint }: { label: string; value: number | null; onChange: (v: number | null) => void; min?: number; max?: number; hint?: string }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
    <InputNumber value={value} onValueChange={(e) => onChange(e.value ?? null)} min={min} max={max} useGrouping={false} className="w-full" />
    {hint && <span className="text-xs text-ink-muted">{hint}</span>}
  </div>
);

/** Spec §20.2 — RAG Designer: General · Knowledge · Retrieval · Generation · Evaluation · Versions.
 *  General + Evaluation edit rag_master. Knowledge + Retrieval + Generation compose a NEW immutable
 *  version (a version is never edited); Versions lists them. */
const RagForm = () => {
  const { id, isReady, navigateToList, navigateToView } = useSecureNavigation({ entity: 'rag', basePath: '/rags' });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const card = `${PANEL} ui-rise p-4`;

  const { control, handleSubmit, reset, watch, setValue, formState: { errors, isDirty } } = useForm<MasterForm>({
    resolver: zodResolver(masterSchema),
    defaultValues: { ragCode: '', name: '', description: '', status: 'ACTIVE', appId: '', defaultEvalSuiteRef: '', requireEvalPass: true },
    mode: 'onChange',
  });

  const { data: accessibleProjects = [] } = useQuery({ queryKey: ['projects', 'accessible'], queryFn: projectsService.getAccessible, staleTime: 5 * 60 * 1000 });
  const projectOptions = accessibleProjects.map((p) => ({ value: p._id, label: p.appCode ? `${p.name} (${p.appCode})` : p.name }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => { if (!isEditMode && !selectedAppId && accessibleProjects.length) setValue('appId', accessibleProjects[0]._id); }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  const { data: suitesResp } = useQuery({ queryKey: ['eval-suites', 'rag'], queryFn: () => evalSuitesService.list({ page: 1, page_size: 100, type: 'rag' } as any), staleTime: 60 * 1000 });
  const suiteOptions = ((suitesResp as any)?.data || []).map((s: any) => ({ value: s._id, label: `${s.suiteCode || s.name}${s.name && s.suiteCode ? ` · ${s.name}` : ''}` }));

  const { data: rag, isLoading } = useQuery({ queryKey: ['rags', id], queryFn: () => ragsService.getById(id!), enabled: isEditMode && isReady && !!id });
  const { data: versions = [], refetch: refetchVersions } = useQuery({ queryKey: ['rag-versions', id], queryFn: () => ragsService.listVersions(id!), enabled: isEditMode && isReady && !!id });
  useBreadcrumbTitle(isEditMode ? rag?.name : null);

  useEffect(() => {
    if (rag && isEditMode) reset({ ragCode: rag.ragCode, name: rag.name, description: rag.description || '', status: rag.status, appId: rag.appId || '',
      defaultEvalSuiteRef: rag.defaultEvalSuiteRef || '', requireEvalPass: rag.activationPolicy?.requireEvalPass ?? true });
  }, [rag, isEditMode, reset]);

  const saveMaster = useMutation({
    mutationFn: (data: any) => (isEditMode && id ? ragsService.update(id, data) : ragsService.create(data)),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['rags'] });
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: `RAG ${isEditMode ? 'updated' : 'created'}`, life: 3000 });
      if (!isEditMode) setTimeout(() => navigateToView(saved._id), 600);
    },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Failed to save the RAG'), life: 10000 }),
  });
  const onSubmit = (d: MasterForm) => {
    const payload: any = { ragCode: d.ragCode, name: d.name, description: d.description || null, status: d.status,
      defaultEvalSuiteRef: d.defaultEvalSuiteRef || null, activationPolicy: { requireEvalPass: d.requireEvalPass } };
    if (!isEditMode && d.appId) payload.appId = d.appId;
    saveMaster.mutate(payload);
  };

  // ---------------- New version composer (Knowledge · Retrieval · Generation) ----------------
  const [pipelineId, setPipelineId] = useState('');
  const [artifactId, setArtifactId] = useState('');
  const [topK, setTopK] = useState<number | null>(8);
  const [minScore, setMinScore] = useState<number | null>(0);
  const [maxChunks, setMaxChunks] = useState<number | null>(8);
  const [maxTokens, setMaxTokens] = useState<number | null>(12000);
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const [rerankOn, setRerankOn] = useState(false);
  const [rerankModel, setRerankModel] = useState('');
  const [poolMultiplier, setPoolMultiplier] = useState<number | null>(3);
  // Reranker budgets stored on this version; null = the platform default.
  const [rerankMaxTokens, setRerankMaxTokens] = useState<number | null>(null);
  const [rerankChars, setRerankChars] = useState<number | null>(null);
  const [rerankCandidates, setRerankCandidates] = useState<number | null>(null);
  const [citeEnabled, setCiteEnabled] = useState(true);
  const [citeFile, setCiteFile] = useState(true);
  const [citePage, setCitePage] = useState(true);
  const [genEnabled, setGenEnabled] = useState(true);
  const [promptId, setPromptId] = useState('');
  const [promptVersion, setPromptVersion] = useState<number | null>(null);
  const [modelRef, setModelRef] = useState('');
  const [answerMax, setAnswerMax] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  const { data: pipelinesResp } = useQuery({ queryKey: ['pipelines', 'names'], queryFn: () => pipelinesService.list({ page: 1, page_size: 100 }), enabled: isEditMode, staleTime: 60 * 1000 });
  const pipelineOptions = (pipelinesResp?.data || []).map((p) => ({ value: p._id, label: `${p.pipelineCode} · ${p.name}` }));
  const { data: artifacts = [] } = useQuery({ queryKey: ['pipeline-artifacts', pipelineId, 'READY'], queryFn: () => pipelinesService.listArtifacts(pipelineId, 'READY'), enabled: !!pipelineId, staleTime: 30 * 1000 });
  // A partial index (a retry of a few sources) is not the corpus; the backend refuses it too.
  const artifactOptions = artifacts.filter((a) => !a.partial)
    .map((a) => ({ value: a._id, label: `${a.artifactCode} · ${a.chunkCount} chunks · ${a.embeddingModel}` }));
  const { data: promptsResp } = useQuery({ queryKey: ['prompts', 'names'], queryFn: () => promptsService.list({ page: 1, page_size: 100 }), enabled: isEditMode, staleTime: 60 * 1000 });
  const promptOptions = ((promptsResp as any)?.data || []).map((p: any) => ({ value: p._id, label: `${p.name}${p.type ? ` (${p.type})` : ''}` }));
  const { data: modelsResp } = useQuery({ queryKey: ['ai-models', 'runnable'], queryFn: () => aiModelsService.list({ page: 1, page_size: 100, runnable_only: true }), enabled: isEditMode, staleTime: 60 * 1000 });
  const modelOptions = (modelsResp?.data || []).map((m: any) => ({ value: m._id, label: `${m.displayName} · ${m.providerType}` }));
  const { data: promptVersions = [] } = useQuery({ queryKey: ['prompt-versions', promptId], queryFn: () => promptsService.listVersions(promptId), enabled: isEditMode && !!promptId, staleTime: 60 * 1000, retry: false });
  const promptVersionOptions = [
    { value: '', label: 'Whichever version is active' },
    ...promptVersions.filter((v) => v.status !== 'DISCARDED')
      .map((v) => ({ value: String(v.version), label: `v${v.version} · ${v.status}${v.tags?.length ? ` · ${v.tags.join(', ')}` : ''}` })),
  ];
  useEffect(() => { setPromptVersion(null); }, [promptId]);
  useEffect(() => { if (!promptId) { const d = ((promptsResp as any)?.data || []).find((p: any) => p.type === 'RAG'); if (d) setPromptId(d._id); } }, [promptsResp]); // eslint-disable-line react-hooks/exhaustive-deps

  const versionProblem = useMemo(() => {
    if (!artifactId) return 'Pick a READY artifact on the Knowledge tab.';
    if ((maxChunks ?? 0) > (topK ?? 0)) return 'Max context chunks cannot exceed Top-K.';
    if (genEnabled && !promptId) return 'Pick a prompt on the Generation tab, or disable generation.';
    if (topK == null) return 'Set Top-K on the Retrieval tab.';
    if (rerankOn && poolMultiplier == null) return 'Set how deep the reranker looks on the Retrieval tab.';
    return null;
  }, [artifactId, maxChunks, topK, genEnabled, promptId, rerankOn, poolMultiplier]);

  const createVersion = useMutation({
    mutationFn: () => {
      const body: RagVersionCreateRequest = {
        pipelineArtifactRef: artifactId,
        retrievalConfig: { retrievalType: 'VECTOR', topK, minimumScore: minScore, maxContextChunks: maxChunks, maxContextTokens: maxTokens, candidateMultiplier: poolMultiplier ?? 3, reranking: { enabled: rerankOn, modelRef: rerankOn ? (rerankModel || null) : null, maxTokens: rerankOn ? rerankMaxTokens : null, passageChars: rerankOn ? rerankChars : null, maxCandidates: rerankOn ? rerankCandidates : null }, filters: { enabled: filtersEnabled } },
        citationConfig: { enabled: citeEnabled, includeFileName: citeFile, includePageNumber: citePage },
        generationConfig: { enabled: genEnabled, promptRef: { promptId: genEnabled ? promptId : null, version: genEnabled ? promptVersion : null }, modelRef: modelRef || null, maxTokens: answerMax },
        notes: notes || null,
      };
      return ragsService.createVersion(id!, body);
    },
    onSuccess: (v) => { toast.current?.show({ severity: 'success', summary: 'Version created', detail: `v${v.version} is a CANDIDATE — evaluate it, then activate.`, life: 5000 }); refetchVersions(); },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Could not create the version'), life: 10000 }),
  });

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });
  if (isEditMode && isLoading) return <PageSkeleton panels={1} />;

  return (
    <div className="p-4" data-testid="SCR-Rag-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader
        icon="pi pi-database"
        onBack={() => handleNavigate('/rags')}
        title={isEditMode ? 'RAG Designer' : 'Create RAG'}
        right={isEditMode && rag && (
          <>
            <span className="font-mono text-xs text-ink-muted">{rag.ragCode}</span>
            <StatusChip tone={rag.activeVersion != null ? 'info' : 'neutral'} dot={false}>
              {rag.activeVersion != null ? `serving v${rag.activeVersion}` : 'not serving'}
            </StatusChip>
          </>
        )}
      />

      <TabView>
        <TabPanel header="General">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
            <div className={`${card} grid grid-cols-1 sm:grid-cols-2 gap-3`}>
              <Controller name="ragCode" control={control} render={({ field }) => <Input label="RAG Code *" {...field} error={errors.ragCode?.message} placeholder="RAG.Police.PenalCode" disabled={isEditMode} />} />
              <Controller name="name" control={control} render={({ field }) => <Input label="Name *" {...field} error={errors.name?.message} />} />
              <Controller name="status" control={control} render={({ field }) => <FormSelect label="Status" name={field.name} value={field.value || 'ACTIVE'} options={[{ label: 'Active', value: 'ACTIVE' }, { label: 'Inactive', value: 'INACTIVE' }]} onChange={field.onChange} />} />
              {!isEditMode && <Controller name="appId" control={control} render={({ field }) => <FormSelect label="Project" name={field.name} value={field.value || ''} options={projectOptions} onChange={field.onChange} disabled={projectLocked} />} />}
              <Controller name="description" control={control} render={({ field }) => <div className="sm:col-span-2"><TextArea label="Description" {...field} rows={2} /></div>} />
            </div>
            <div className="flex justify-end gap-2">
              <span className="ui-press inline-flex"><Button type="submit" label={saveMaster.isPending ? 'Saving…' : isEditMode ? 'Save' : 'Create RAG'} icon={saveMaster.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={saveMaster.isPending} /></span>
            </div>
            {!isEditMode && <p className="text-xs text-ink-muted">Create the RAG first; the Knowledge, Retrieval and Generation tabs then compose its first version.</p>}
          </form>
        </TabPanel>

        <TabPanel header="Knowledge" disabled={!isEditMode}>
          <div className={`${card} grid grid-cols-1 sm:grid-cols-2 gap-3`}>
            <FormSelect label="Pipeline" name="pipeline" value={pipelineId} options={pipelineOptions} onChange={(e: any) => { setPipelineId(e.target ? e.target.value : e); setArtifactId(''); }} filter />
            <FormSelect label="Artifact (READY) *" name="artifact" value={artifactId} options={artifactOptions} onChange={(e: any) => setArtifactId(e.target ? e.target.value : e)} disabled={!pipelineId}
                        helperText={pipelineId && !artifactOptions.length ? 'This pipeline has no READY artifact yet — run it first.' : 'The version binds to this exact artifact; a new artifact needs a new version.'} />
          </div>
        </TabPanel>

        <TabPanel header="Retrieval" disabled={!isEditMode}>
          <div className={`${card} grid grid-cols-1 sm:grid-cols-2 gap-3`}>
            <Num label="Top-K" value={topK} onChange={setTopK} min={1} max={100} hint="Candidates asked of the index" />
            <Num label="Minimum score (0–1)" value={minScore} onChange={setMinScore} min={0} max={1} hint="Cosine similarity below this is dropped" />
            <Num label="Max context chunks" value={maxChunks} onChange={setMaxChunks} min={1} max={100} hint="Cap after scoring; must be ≤ Top-K" />
            <Num label="Max context tokens" value={maxTokens} onChange={setMaxTokens} min={200} max={200000} hint="Estimated at 4 chars/token" />
            <div className="sm:col-span-2">
              <Switch label="Allow callers to filter by source" value={filtersEnabled} onChange={setFiltersEnabled} />
            </div>
            <div className="sm:col-span-2 border-t border-line pt-3">
              <Switch label="Rerank results" value={rerankOn} onChange={setRerankOn}
                      hint="A model reads the question with every candidate and reorders them by how well they answer it, before the context is chosen. More accurate; adds one model call per question." />
            </div>
            {rerankOn && (
              <>
                <FormSelect label="Rerank model" name="rerankModel" value={rerankModel} options={modelOptions}
                            onChange={(e: any) => setRerankModel(e.target ? e.target.value : e)} showClear filter
                            helperText="Blank = the answer model. A fast model keeps questions quick." />
                <Num label="Rerank pool (× Top-K)" value={poolMultiplier} onChange={setPoolMultiplier} min={1} max={10}
                     hint={`Reranks the top ${(topK ?? 0) * (poolMultiplier ?? 0)} candidates, then keeps the best ${maxChunks ?? 0}`} />
                <Num label="Passages reranked per question" value={rerankCandidates} onChange={setRerankCandidates} min={2} max={200}
                     hint="Blank = 50. The rest keep their retrieval order after them." />
                <Num label="Characters read per passage" value={rerankChars} onChange={setRerankChars} min={100} max={8000}
                     hint="Blank = 1200. More = better judgement, more tokens per question." />
                <Num label="Rerank output tokens" value={rerankMaxTokens} onChange={setRerankMaxTokens} min={64} max={32000}
                     hint="Blank = 2048. Raise it if a reasoning model is the reranker and reranking gets skipped." />
              </>
            )}
          </div>
        </TabPanel>

        <TabPanel header="Generation" disabled={!isEditMode}>
          <div className={`${card} grid grid-cols-1 sm:grid-cols-2 gap-3`}>
            <div className="sm:col-span-2"><Switch label="Generate an answer" hint="Off = retrieval-only RAG (context service)" value={genEnabled} onChange={setGenEnabled} /></div>
            <FormSelect label="Prompt *" name="prompt" value={promptId} options={promptOptions} onChange={(e: any) => setPromptId(e.target ? e.target.value : e)} disabled={!genEnabled} filter
                        helperText="From Prompt Management. Must use {{context}} and {{question}}; the seeded 'RAG Answer' prompt does." />
            <FormSelect label="Answer model" name="model" value={modelRef} options={modelOptions} onChange={(e: any) => setModelRef(e.target ? e.target.value : e)} disabled={!genEnabled} showClear filter
                        helperText="Blank = the platform's default completion model" />
            <FormSelect label="Prompt version" name="promptVersion" value={promptVersion == null ? '' : String(promptVersion)}
                        options={promptVersionOptions} disabled={!genEnabled || !promptId}
                        onChange={(e: any) => { const raw = e.target ? e.target.value : e; setPromptVersion(raw ? Number(raw) : null); }}
                        helperText="Active = follows the prompt. A number freezes the text this RAG version answers with." />
            <Num label="Max answer tokens" value={answerMax} onChange={setAnswerMax} min={1} hint="Blank = model default" />
            <div className="sm:col-span-2">
              <Switch label="Citations" value={citeEnabled} onChange={setCiteEnabled} />
              <Switch label="Include file name" value={citeFile} onChange={setCiteFile} />
              <Switch label="Include page number" value={citePage} onChange={setCitePage} />
            </div>
            <div className="sm:col-span-2"><TextArea label="Version notes" value={notes} onChange={(v: string) => setNotes(v)} rows={2} placeholder="Why this version exists" /></div>
            <div className="sm:col-span-2 flex items-center justify-between gap-3">
              {versionProblem
                ? <span className="text-xs font-medium" style={{ color: 'var(--t-warn, #8a6218)' }}>{versionProblem}</span>
                : <span className="text-xs text-ink-muted" style={PRETTY}>Creates a CANDIDATE. Evaluate it, then activate from the Versions tab.</span>}
              <span className="ui-press inline-flex"><Button label={createVersion.isPending ? 'Creating…' : 'Create version'} icon={createVersion.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-plus'} disabled={!!versionProblem || createVersion.isPending} onClick={() => createVersion.mutate()} data-testid="Rag.CreateVersion" /></span>
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Evaluation">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
            <div className={`${card} grid grid-cols-1 gap-3`}>
              <Controller name="defaultEvalSuiteRef" control={control} render={({ field }) => (
                <FormSelect label="Default eval suite" name={field.name} value={field.value || ''} options={suiteOptions} onChange={field.onChange} showClear filter
                            helperText="Activation looks for a PASS from this suite targeting the exact version. Blank = any rag suite." />
              )} />
              <Controller name="requireEvalPass" control={control} render={({ field }) => (
                <Switch label="Require an eval PASS before activation" hint="Off lets a candidate go live untested. Spec §17 recommends on." value={!!field.value} onChange={field.onChange} />
              )} />
              {rag?.lastEvalScore != null && <Message severity="info" className="w-full" text={`Last gating score: ${(rag.lastEvalScore * 100).toFixed(0)}%`} />}
            </div>
            <div className="flex justify-end"><span className="ui-press inline-flex"><Button type="submit" label={saveMaster.isPending ? 'Saving…' : 'Save'} icon={saveMaster.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={saveMaster.isPending} /></span></div>
          </form>
        </TabPanel>

        <TabPanel header={`Versions${versions.length ? ` (${versions.length})` : ''}`} disabled={!isEditMode}>
          <Rise className={`${PANEL} overflow-hidden`}>
            {versions.length === 0 ? (
              <EmptyState icon="pi pi-clone" title="No versions yet"
                          hint="Compose one on the Knowledge, Retrieval and Generation tabs, then create it from there." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      <th className={fth}>Version</th><th className={fth}>Status</th><th className={fth}>Artifact</th>
                      <th className={`${fth} text-right`}>Top-K / min</th><th className={fth}>Generation</th>
                      <th className={`${fth} text-right`}>Eval</th><th className={fth}>Created</th>
                    </tr>
                  </thead>
                  <tbody>{versions.map((v) => (
                    <tr key={v._id} className="ui-row border-b border-gray-50 last:border-0 dark:border-gray-700">
                      <td className={`${ftd} font-mono tabular-nums text-ink`}>v{v.version}</td>
                      <td className={ftd}><StatusChip tone={versionTone(v.status)}>{v.status}</StatusChip></td>
                      <td className={`${ftd} font-mono text-xs text-gray-600 dark:text-gray-300`}>{v.artifactCode}</td>
                      <td className={`${ftd} text-right tabular-nums`}>
                        {v.retrievalConfig?.topK} / {v.retrievalConfig?.minimumScore}
                        {v.retrievalConfig?.reranking?.enabled && <span className="ui-tone ui-tone--info ml-1.5">reranked</span>}
                      </td>
                      <td className={ftd}>{v.generationConfig?.enabled ? 'On' : 'Retrieval only'}</td>
                      <td className={`${ftd} text-right tabular-nums`}>{v.evalScore != null ? `${(v.evalScore * 100).toFixed(0)}%` : '—'}</td>
                      <td className={`${ftd} tabular-nums text-gray-600 dark:text-gray-300`}>{formatDate(v.createdAt)}</td>
                    </tr>))}</tbody>
                </table>
              </div>
            )}
            {isEditMode && id && (
              <p className="border-t border-gray-100 px-4 py-3 text-xs leading-relaxed text-gray-500 dark:border-gray-700 dark:text-gray-400" style={PRETTY}>
                Activate, roll back and test versions from the RAG's view page.
              </p>
            )}
          </Rise>
        </TabPanel>
      </TabView>

      <div className="mt-3 flex justify-end"><span className="ui-press inline-flex"><Button label="Done" severity="secondary" outlined onClick={() => (isEditMode && id ? navigateToView(id) : navigateToList())} /></span></div>
      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave} />
    </div>
  );
};

export default RagForm;
